import { useMutation } from '@apollo/client';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import {
  AlertMessageDialog,
  DialogVariants,
} from '@website/components/utilities/AlertMessageDialog';
import { preventURLsValidator } from '@website/libs/validator/preventURLsValidator';
import {
  BroadcastMessageToGuestsInGroupInput,
  SendBroadcastMessageToGroupDocument,
} from '@website/types/graphqlOperations';
import { useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

type Props = {
  groupID: string;
};

export default function BroadcastMessageToGroupForm({ groupID }: Props) {
  const [message, setMessage] = useState('');

  const [sendBroadcastMessageToGroup] = useMutation(
    SendBroadcastMessageToGroupDocument,
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm<BroadcastMessageToGuestsInGroupInput>({
    mode: 'onChange',
    defaultValues: {
      groupID,
    },
  });

  const sendBroadcastMessage = async (
    values: BroadcastMessageToGuestsInGroupInput,
  ) => {
    try {
      await sendBroadcastMessageToGroup({
        variables: {
          input: { ...values, groupID },
        },
      });
      reset();
      setMessage('Message was successfully sent to guests in the Group');
    } catch (e) {
      if (e.networkError) {
        alert(
          'Internet connection lost - please retry when you are connected.',
        );
      } else {
        alert(e);
      }
    }
  };

  return (
    <>
      {message && (
        <AlertMessageDialog
          title="Message Sent!"
          body={message}
          isModalVisible={!!message}
          variant={DialogVariants.SUCCESS}
          onHideModal={() => setMessage('')}
        />
      )}
      <Form onSubmit={handleSubmit(sendBroadcastMessage)}>
        <Form.Group controlId="message">
          <Form.Label>Message</Form.Label>
          <Form.Control
            rows={6}
            name="text"
            as="textarea"
            {...register('text', {
              required: true,
              validate: preventURLsValidator,
            })}
            isInvalid={!!errors.text}
          />

          {errors.text && errors.text.type === 'validate' && (
            <Form.Control.Feedback type="invalid">
              Please don't send links or emails. All trip information should be
              setup on the trip page (e.g. with FAQs), and all communication
              should be through the Skyhook platform.
            </Form.Control.Feedback>
          )}

          {errors.text && errors.text.type === 'required' && (
            <Form.Control.Feedback type="invalid">
              Please enter the message you would like to send.
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Row>
          <Col className="mt-2">
            <SubmitButton
              isSubmitting={isSubmitting}
              submittingLabel="Sending Message"
              text="Send Message"
            ></SubmitButton>
          </Col>
        </Row>
      </Form>
    </>
  );
}
